import { z } from "zod";
import { viewConfigSchemaForTaskType } from "./close-template-task-view-config.schema";
import { TaskPriority, TaskType } from "../types/task";
import {
    TaskTemplateAssigneeRole,
    TaskTemplateDeadlineAnchor,
    TaskTemplateSchema,
} from "../types/taskTemplate";

/**
 * Max length of a task-template `initialMessage`. Enforced on the request
 * body here and again on the service's *normalized* (Slate-wrapped) value so a
 * plain-text message that would overflow once wrapped is rejected on write —
 * keeping the stored/returned document round-trippable through this same limit.
 */
export const TASK_TEMPLATE_INITIAL_MESSAGE_MAX_LENGTH = 50000;

/**
 * Cross-field refinement: deadlineAnchor requires externalDeadline to be set.
 * Rejects payloads where deadlineAnchor is non-null but externalDeadline is
 * explicitly null, preventing semantically orphaned anchors in the DB.
 */
export function refineDeadlineAnchor(
    data: {
        deadlineAnchor?: TaskTemplateDeadlineAnchor | null;
        externalDeadline?: number | null;
    },
    ctx: z.RefinementCtx,
): void {
    if (data.deadlineAnchor != null && data.externalDeadline === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "deadlineAnchor requires externalDeadline to be set",
            path: ["deadlineAnchor"],
        });
    }
}

export const viewConfigValueSchema = z.custom<object | null>(
    (value) =>
        value === null || (typeof value === "object" && !Array.isArray(value)),
    { message: "viewConfig must be an object or null" },
);

/**
 * Create-time refinement: `viewConfig` must be valid for the task `type` in the
 * same payload (a view-bearing type requires its document, a question type
 * requires none). Creates always carry the type, so validation is fully
 * decidable here.
 *
 * Updates deliberately do NOT run this: task type is immutable, and a
 * `viewConfig`-only PATCH is validated against the stored type in
 * `TaskTemplateService.updateTaskTemplate`.
 */
export function refineViewConfigForCreate(
    data: {
        type?: TaskType | null;
        viewConfig?: object | null;
    },
    ctx: z.RefinementCtx,
): void {
    const parsed = viewConfigSchemaForTaskType(data.type ?? null).safeParse(
        data.viewConfig ?? null,
    );
    if (parsed.success) {
        return;
    }
    for (const issue of parsed.error.issues) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: ["viewConfig", ...issue.path],
        });
    }
}

export const TaskTemplateListQuerySchema = z.object({
    offset: z.coerce.number().int().min(0).default(0),
    closeTemplateId: z.string().uuid().optional(),
});

export const TemplatePathParamsSchema = z.object({
    templateId: z.string().uuid(),
});

const BULK_SAVE_MAX_EDGES = 500;

const TaskTemplateEdgeIdSchema = z
    .string()
    .uuid()
    .transform((id) => id.toLowerCase());

export const TaskTemplateLinkIdsSchema = z
    .array(TaskTemplateEdgeIdSchema)
    .max(BULK_SAVE_MAX_EDGES);

export const TaskTemplateCreateSchema = z
    .object({
        title: z.string().min(1, { message: "Required" }).max(255),
        description: z.string().max(5000).nullable().optional(),
        priority: z.nativeEnum(TaskPriority).nullable().optional(),
        groupId: z.string().uuid().nullable().optional(),
        assigneeId: z.number().int().nullable().optional(),
        assigneeRole: z
            .nativeEnum(TaskTemplateAssigneeRole)
            .nullable()
            .optional(),
        externalDeadline: z
            .number()
            .int()
            .min(0)
            .max(365)
            .nullable()
            .optional(),
        deadlineAnchor: z
            .nativeEnum(TaskTemplateDeadlineAnchor)
            .nullable()
            .optional(),
        estimatedTime: z.number().int().min(0).nullable().optional(),
        type: z.nativeEnum(TaskType).nullable().optional(),
        initialMessage: z
            .string()
            .max(TASK_TEMPLATE_INITIAL_MESSAGE_MAX_LENGTH)
            .nullable()
            .optional(),
        closeTemplateId: z.string().uuid().nullable().optional(),
        closeStageId: z.string().uuid().nullable().optional(),
        viewConfig: viewConfigValueSchema.nullable().optional(),
        sourceTemplateId: z.string().uuid().nullable().optional(),
        blockedByTaskTemplateIds: TaskTemplateLinkIdsSchema.optional(),
        relatedTaskTemplateIds: TaskTemplateLinkIdsSchema.optional(),
    })
    .superRefine(refineDeadlineAnchor)
    .superRefine(refineViewConfigForCreate);

export type TaskTemplateCreate = z.infer<typeof TaskTemplateCreateSchema>;

const TaskTemplateUpdateFieldsSchema = z.object({
    title: z.string().min(1).max(255).optional(),
    description: z.string().max(5000).nullable().optional(),
    priority: z.nativeEnum(TaskPriority).nullable().optional(),
    groupId: z.string().uuid().nullable().optional(),
    assigneeId: z.number().int().nullable().optional(),
    assigneeRole: z.nativeEnum(TaskTemplateAssigneeRole).nullable().optional(),
    externalDeadline: z.number().int().min(0).max(365).nullable().optional(),
    deadlineAnchor: z
        .nativeEnum(TaskTemplateDeadlineAnchor)
        .nullable()
        .optional(),
    estimatedTime: z.number().int().min(0).nullable().optional(),
    type: z.nativeEnum(TaskType).nullable().optional(),
    initialMessage: z
        .string()
        .max(TASK_TEMPLATE_INITIAL_MESSAGE_MAX_LENGTH)
        .nullable()
        .optional(),
    closeStageId: z.string().uuid().nullable().optional(),
    viewConfig: viewConfigValueSchema.nullable().optional(),
});

export const TaskTemplateUpdateSchema = TaskTemplateUpdateFieldsSchema.extend({
    blockedByTaskTemplateIds: TaskTemplateLinkIdsSchema.optional(),
    relatedTaskTemplateIds: TaskTemplateLinkIdsSchema.optional(),
}).superRefine(refineDeadlineAnchor);

export type TaskTemplateUpdate = z.infer<typeof TaskTemplateUpdateSchema>;

export const TaskTemplatePreviewRequestSchema = z.object({
    overrides:
        TaskTemplateUpdateFieldsSchema.superRefine(
            refineDeadlineAnchor,
        ).optional(),
});

export type TaskTemplatePreviewRequest = z.infer<
    typeof TaskTemplatePreviewRequestSchema
>;

export const TaskTemplateBulkSaveCreateItemSchema = z
    .object({
        localId: z.string().min(1).max(255),
        title: z.string().min(1, { message: "Required" }).max(255),
        assigneeId: z.number().int().nullable().optional(),
        assigneeRole: z
            .nativeEnum(TaskTemplateAssigneeRole)
            .nullable()
            .optional(),
        externalDeadline: z
            .number()
            .int()
            .min(0)
            .max(365)
            .nullable()
            .optional(),
        deadlineAnchor: z
            .nativeEnum(TaskTemplateDeadlineAnchor)
            .nullable()
            .optional(),
        estimatedTime: z.number().int().min(0).nullable().optional(),
        type: z.nativeEnum(TaskType).nullable().optional(),
        initialMessage: z
            .string()
            .max(TASK_TEMPLATE_INITIAL_MESSAGE_MAX_LENGTH)
            .nullable()
            .optional(),
        closeStageId: z.string().uuid().nullable().optional(),
        viewConfig: viewConfigValueSchema.nullable().optional(),
        sourceTemplateId: z.string().uuid().nullable().optional(),
        order: z.number().int().min(0).max(2147483647).optional(),
    })
    .superRefine(refineDeadlineAnchor)
    .superRefine(refineViewConfigForCreate);

export const TaskTemplateBulkSaveUpdateItemSchema = z
    .object({
        id: z.string().uuid(),
        title: z.string().min(1).max(255).optional(),
        assigneeId: z.number().int().nullable().optional(),
        assigneeRole: z
            .nativeEnum(TaskTemplateAssigneeRole)
            .nullable()
            .optional(),
        externalDeadline: z
            .number()
            .int()
            .min(0)
            .max(365)
            .nullable()
            .optional(),
        deadlineAnchor: z
            .nativeEnum(TaskTemplateDeadlineAnchor)
            .nullable()
            .optional(),
        estimatedTime: z.number().int().min(0).nullable().optional(),
        type: z.nativeEnum(TaskType).nullable().optional(),
        initialMessage: z
            .string()
            .max(TASK_TEMPLATE_INITIAL_MESSAGE_MAX_LENGTH)
            .nullable()
            .optional(),
        closeStageId: z.string().uuid().nullable().optional(),
        viewConfig: viewConfigValueSchema.nullable().optional(),
        appliedSourceTemplateVersion: z
            .number()
            .int()
            .min(1)
            .max(2147483647)
            .optional(),
        order: z.number().int().min(0).max(2147483647).optional(),
    })
    .superRefine(refineDeadlineAnchor);

const BULK_SAVE_MAX_ITEMS = 100;

export const TaskTemplateEdgeRefSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("persisted"), id: TaskTemplateEdgeIdSchema }),
    z.object({
        kind: z.literal("local"),
        localId: z.string().min(1).max(255),
    }),
]);

export const TaskTemplateDependencyAddSchema = z.object({
    blocking: TaskTemplateEdgeRefSchema,
    blocked: TaskTemplateEdgeRefSchema,
});

export const TaskTemplateDependencyRemoveSchema = z.object({
    blockingId: TaskTemplateEdgeIdSchema,
    blockedId: TaskTemplateEdgeIdSchema,
});

export const TaskTemplateRelationAddSchema = z.object({
    a: TaskTemplateEdgeRefSchema,
    b: TaskTemplateEdgeRefSchema,
});

export const TaskTemplateRelationRemoveSchema = z.object({
    aId: TaskTemplateEdgeIdSchema,
    bId: TaskTemplateEdgeIdSchema,
});

export const TaskTemplateDependenciesChangeSchema = z.object({
    adds: z
        .array(TaskTemplateDependencyAddSchema)
        .max(BULK_SAVE_MAX_EDGES)
        .default([]),
    removes: z
        .array(TaskTemplateDependencyRemoveSchema)
        .max(BULK_SAVE_MAX_EDGES)
        .default([]),
});

export const TaskTemplateRelationsChangeSchema = z.object({
    adds: z
        .array(TaskTemplateRelationAddSchema)
        .max(BULK_SAVE_MAX_EDGES)
        .default([]),
    removes: z
        .array(TaskTemplateRelationRemoveSchema)
        .max(BULK_SAVE_MAX_EDGES)
        .default([]),
});

export const TaskTemplateBulkSaveSchema = z.object({
    closeTemplateId: z.string().uuid(),
    creates: z
        .array(TaskTemplateBulkSaveCreateItemSchema)
        .max(BULK_SAVE_MAX_ITEMS)
        .default([]),
    updates: z
        .array(TaskTemplateBulkSaveUpdateItemSchema)
        .max(BULK_SAVE_MAX_ITEMS)
        .default([]),
    deletes: z.array(z.string().uuid()).max(BULK_SAVE_MAX_ITEMS).default([]),
    dependencies: TaskTemplateDependenciesChangeSchema.optional(),
    relations: TaskTemplateRelationsChangeSchema.optional(),
});

export const TaskTemplateBulkSaveResponseSchema = z.object({
    created: z.record(z.string(), TaskTemplateSchema),
    updated: z.array(z.string().uuid()),
    deleted: z.array(z.string().uuid()),
});

export type TaskTemplateEdgeRef = z.infer<typeof TaskTemplateEdgeRefSchema>;
export type TaskTemplateDependencyAdd = z.infer<
    typeof TaskTemplateDependencyAddSchema
>;
export type TaskTemplateDependencyRemove = z.infer<
    typeof TaskTemplateDependencyRemoveSchema
>;
export type TaskTemplateRelationAdd = z.infer<
    typeof TaskTemplateRelationAddSchema
>;
export type TaskTemplateRelationRemove = z.infer<
    typeof TaskTemplateRelationRemoveSchema
>;

export type TaskTemplateBulkSave = z.infer<typeof TaskTemplateBulkSaveSchema>;
export type TaskTemplateBulkSaveCreateItem = z.infer<
    typeof TaskTemplateBulkSaveCreateItemSchema
>;
export type TaskTemplateBulkSaveUpdateItem = z.infer<
    typeof TaskTemplateBulkSaveUpdateItemSchema
>;
